import * as React from 'react';
import { format } from 'date-fns';
import type { Locale } from 'date-fns';
import type { Matcher } from 'react-day-picker';
import { buildDateRangeMatchers, parseDateStr, toDateStr } from '../lib/date-string.js';
import {
  DatePickerPopoverContent,
  DatePickerTrigger,
  useCalendarPopoverSide,
} from './date-picker-trigger.js';
import { Popover } from './popover.js';
import { Calendar } from './calendar.js';

export interface DatePickerButtonProps {
  /** Selected date as a YYYY-MM-DD string, or '' when unset. */
  value: string;
  onChange: (next: string) => void;
  /** Shown in the trigger when no date is selected. Pass a translated string. */
  placeholder?: string;
  /** date-fns format pattern used to render the selected date. Defaults to 'd MMM yyyy'. */
  formatPattern?: string;
  /** Locale for both the formatted trigger label and the Calendar's month/weekday names. */
  locale?: Locale;
  disabled?: boolean;
  /** Forwarded to the trigger Button so a `<Label htmlFor>` can target it. */
  id?: string;
  /** Forwarded to the trigger Button for screen-reader field naming. */
  'aria-label'?: string;
  testId?: string;
  /** YYYY-MM-DD string. Dates before this are disabled/unselectable in the Calendar. */
  minDate?: string;
  /** YYYY-MM-DD string. Dates after this are disabled/unselectable in the Calendar. */
  maxDate?: string;
  /**
   * YYYY-MM-DD string used to seed the initially displayed month when `value`
   * is empty (e.g. the counterpart date of a from/to pair, so opening the
   * "to" picker lands near the already-picked "from" date instead of today).
   * Ignored once `value` is set.
   */
  anchorDate?: string;
  /**
   * Forwarded to react-day-picker's single-mode `required` flag. When true,
   * clicking the already-selected day again does NOT deselect/clear it — the
   * Calendar's `onSelect` can then never fire with an empty value. Use for
   * fields that must always hold a date once one is picked (e.g. a mandatory
   * `validUntil`). Defaults to false (current behavior: re-clicking the
   * selected day clears it), so every existing caller is unaffected.
   */
  required?: boolean;
}

/**
 * shadcn-style date picker: a Button trigger showing the formatted selected
 * date, opening a Popover containing the Calendar. Generic — takes all
 * user-facing text as props.
 */
export function DatePickerButton({
  value,
  onChange,
  placeholder = 'Select date',
  formatPattern = 'd MMM yyyy',
  locale,
  disabled,
  id,
  'aria-label': ariaLabel,
  testId,
  minDate,
  maxDate,
  anchorDate,
  required = false,
}: DatePickerButtonProps) {
  const [open, setOpen] = React.useState(false);
  const selectedDate = parseDateStr(value);
  const anchorDateObj = parseDateStr(anchorDate ?? '');
  const isOpen = open && !disabled;
  const { triggerRef, contentRef, side, align } = useCalendarPopoverSide();
  const [displayMonth, setDisplayMonth] = React.useState(
    () => selectedDate ?? anchorDateObj ?? new Date(),
  );
  // Tracks the last `value`/`anchorDate` we've resynced `displayMonth` to, so
  // we can tell an external change apart from the user's own month navigation.
  const syncedValueRef = React.useRef(value);
  const syncedAnchorRef = React.useRef(anchorDate);

  // Resync the viewed month when `value` or `anchorDate` changes externally.
  // While the popover is open we only record that a resync is pending (via
  // the refs staying stale) so we don't jump the view out from under a user
  // who is actively navigating months; the resync then fires as soon as the
  // popover closes, even if neither changes again in the meantime.
  React.useEffect(() => {
    const valueChanged = value !== syncedValueRef.current;
    const anchorChanged = anchorDate !== syncedAnchorRef.current;
    if ((!valueChanged && !anchorChanged) || open) {
      return;
    }
    setDisplayMonth(selectedDate ?? anchorDateObj ?? new Date());
    syncedValueRef.current = value;
    syncedAnchorRef.current = anchorDate;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `selectedDate`/`anchorDateObj` are derived from `value`/`anchorDate` each render; including them would re-run this effect for no reason
  }, [value, anchorDate, open]);

  const disabledMatchers = React.useMemo<Matcher[] | undefined>(() => {
    const matchers = buildDateRangeMatchers(minDate, maxDate);
    return matchers.length > 0 ? matchers : undefined;
  }, [minDate, maxDate]);

  return (
    <Popover open={isOpen} onOpenChange={(next) => setOpen(next && !disabled)}>
      <DatePickerTrigger
        ref={triggerRef}
        disabled={disabled}
        id={id}
        aria-label={ariaLabel}
        testId={testId}
      >
        {selectedDate ? (
          format(selectedDate, formatPattern, locale ? { locale } : undefined)
        ) : (
          <span className="text-muted-foreground">{placeholder}</span>
        )}
      </DatePickerTrigger>
      <DatePickerPopoverContent ref={contentRef} side={side} align={align}>
        <Calendar
          mode="single"
          selected={selectedDate}
          month={displayMonth}
          onMonthChange={setDisplayMonth}
          onSelect={(date: Date | undefined) => {
            onChange(date ? toDateStr(date) : '');
            setOpen(false);
          }}
          disabled={disabledMatchers}
          required={required}
          {...(locale ? { locale } : {})}
        />
      </DatePickerPopoverContent>
    </Popover>
  );
}
